import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import ClientShell from "@/components/ClientShell";
import { ChatCircleText, PlusCircle, ArrowUpRight } from "@phosphor-icons/react";
import { formatDate, formatCurrency, formatDateTime } from "@/lib/format";
import { useCategories } from "@/hooks/useCategories";

const STATUS_META = {
  PENDING_REVIEW: { label: "Pending review", cls: "bg-zinc-100 text-zinc-800 border-zinc-200" },
  IN_REVIEW: { label: "In review", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  QUOTED: { label: "Quote ready", cls: "bg-[#FF5A1F]/10 text-[#E04814] border-[#FF5A1F]/30" },
  APPROVED: { label: "Approved", cls: "bg-green-50 text-green-800 border-green-200" },
  REJECTED: { label: "Rejected", cls: "bg-red-50 text-red-800 border-red-200" },
};

function QuoteStatus({ status }) {
  const s = STATUS_META[status] || STATUS_META.PENDING_REVIEW;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 border text-[0.65rem] font-semibold uppercase tracking-[0.15em] ${s.cls}`}>
      {s.label}
    </span>
  );
}

export default function Quotations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { labelFor } = useCategories();

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/quotations");
      setItems(res.data);
    } catch (e) {
      console.error("[Quotations] fetch failed:", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return (
    <ClientShell>
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="mt-tiny text-[#FF5A1F]">Quotations</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight text-zinc-900 mt-2">
            My quote requests
          </h1>
          <p className="mt-2 text-zinc-600 text-sm">Fill in your requirements — our team reviews and sends a quote with certificates.</p>
        </div>
        <Link
          to="/quotations/new"
          data-testid="new-quotation-button"
          className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#E04814] text-white px-4 py-2.5 font-medium transition-colors shrink-0"
        >
          <PlusCircle size={18} weight="fill" /> New request
        </Link>
      </div>

      {loading ? (
        <div className="grid gap-3">
          {["s-1", "s-2", "s-3"].map((k) => (
            <div key={k} className="h-24 bg-white border border-zinc-200 animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-10 text-center">
          <ChatCircleText size={44} weight="duotone" className="mx-auto text-zinc-300" />
          <div className="mt-3 text-zinc-500 text-sm">No quote requests yet.</div>
          <Link
            to="/quotations/new"
            className="mt-4 inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white px-5 py-2.5 font-medium transition-colors"
          >
            <PlusCircle size={16} weight="fill" /> Request your first quote
          </Link>
        </div>
      ) : (
        <div className="grid gap-3" data-testid="quotations-list">
          {items.map((q) => (
            <Link
              key={q.quotation_id}
              to={`/quotations/${q.quotation_id}`}
              data-testid={`quotation-card-${q.quotation_number}`}
              className="group bg-white border border-zinc-200 hover:border-zinc-900 p-4 md:p-5 flex items-center gap-4 transition-all duration-200 hover:-translate-y-0.5"
            >
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="mt-tiny text-zinc-400 font-mono-mt">{q.quotation_number}</div>
                  <QuoteStatus status={q.status} />
                </div>
                <div className="font-display text-lg font-bold text-zinc-900 mt-1 group-hover:text-[#0047AB] transition-colors truncate">
                  {q.material}
                </div>
                <div className="mt-1 text-xs text-zinc-500 truncate">
                  {labelFor(q.category)}
                  {q.quantity ? ` · Qty ${q.quantity}` : ""}
                  {q.dimensions ? ` · ${q.dimensions}` : ""}
                </div>
              </div>
              <div className="text-right shrink-0">
                {q.quote_amount != null ? (
                  <div className="font-mono-mt text-sm font-semibold text-zinc-900">{formatCurrency(q.quote_amount)}</div>
                ) : (
                  <div className="mt-tiny text-zinc-400">{formatDate(q.created_at)}</div>
                )}
                <ArrowUpRight size={18} className="inline-block text-zinc-400 group-hover:text-[#FF5A1F] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform mt-1" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </ClientShell>
  );
}
